import { useState, useEffect } from "react";
import {
  verifyTokenApi,
  updateCustomerPassword,
  resendOtp,
  verifyCustomerOtp,
} from "../authentication/_request";

// @mui material components
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
// Images
import profileImg from "../../assets/images/icons/catering-icons/profileIcon.svg";

import { useAuth } from "context";
import { getCustomerAPI, updateCustomerAPI, unLinkedCustomerAccount } from "./_request";

import { createLinkedAccountApi, verifyLinkedAccountApi } from "layouts/authentication/_request";
import UpdatePasswordModal from "../../examples/Modal/UpdatePassWordModal";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import { NavbarAlert } from "examples/Alert";
import VerifiedStatus from "components/Custom/VerifiedStatus";
import SecondaryEmail from "layouts/authentication/components/EmailVerification/SecondaryEmail";
import OtpConfirmationModal from "examples/Modal/OtpConfirmationModal";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";

const Profile = () => {
  const [customer, setCustomer] = useState();
  const [workEmail, setWorkEmail] = useState({
    email: "",
    isOTPVisible: false,
    otpNumber: "",
    isVerified: false,
    linkedAccountId: null,
  });
  const [openUpdatePasswordModal, setOpenUpdatePasswordModal] = useState(false);
  const [userPassword, setUserPassword] = useState("");
  const [enableEdit, setEnableEdit] = useState(false);
  const { saveAuthData, showSnackbarWithMessage, setVerifiedCustomer } = useAuth();
  const [loader, setLoader] = useState(true);
  const [spinner, setSpinner] = useState(false);
  const [nameError, setNameError] = useState("");
  const [editProfileLoader, setEditProfileLoader] = useState(false);
  const [confirmationModalLoader, setConfirmationModalLoader] = useState(false);
  const [customerDataLoader, setCustomerDataLoader] = useState(false);

  // New state for OTP modal
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [verificationIdentifier, setVerificationIdentifier] = useState("");
  const [verificationType, setVerificationType] = useState(""); // "email" or "phone"
  // otp field
  const [verificationState, setVerificationState] = useState({
    email: {
      otpNumber: "",
      otpSent: false,
    },
    phone: {
      otpNumber: "",
      otpSent: false,
    },
  });

  // Determine if there are unsaved changes by comparing formData with originalData
  const hasUnsavedChanges = enableEdit;

  // hook to track changes
  const { navigateWithConfirmation, UnsavedChangesDialog } =
    useUnsavedChangesGuard(hasUnsavedChanges);

  const handleClose = () => {
    setOpenUpdatePasswordModal(!openUpdatePasswordModal);
  };

  const updatePassWord = async (newPassword) => {
    try {
      setLoader(true);
      setSpinner(true);
      const payload = {
        newPassword: newPassword,
        userPassword: userPassword,
        forgotPassword: customer?.isPass ? false : true,
      };
      const updatePasswordResponse = await updateCustomerPassword(payload);
      const { success, data, message } = updatePasswordResponse.data;
      if (success) {
        const authData = data.tokens;
        await saveAuthData(authData);
        const verifyResponse = await verifyTokenApi({ accessToken: authData.accessToken });
        const { success: verifySuccess, data: verifyData } = verifyResponse.data;
        if (verifySuccess && !verifyData.verifiedData.defaultPassword) {
          setLoader(false);
          setSpinner(false);
          showSnackbarWithMessage({
            type: "success",
            message: message,
          });
          setOpenUpdatePasswordModal(false);
          await getCustomerDetails();
        }
      } else {
        showSnackbarWithMessage({
          type: "error",
          message: message,
        });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoader(false);
      setSpinner(false);
    }
  };

  const getCustomerDetails = async () => {
    try {
      setCustomerDataLoader(true);
      const {
        data: { success, data },
      } = await getCustomerAPI();
      if (success) {
        setCustomer({
          id: data.customer.id,
          firstName: data.customer.firstName,
          lastName: data.customer.lastName,
          email: data.customer.email,
          verifiedAt: data.customer.verifiedAt,
          phoneVerifiedAt: data.customer.phoneVerifiedAt,
          phoneCountryCode: data.customer.phoneCountryCode,
          phoneNumber: data.customer.phoneNumber,
          isPass: data.customer.isPass,
        });

        // Sync verification status
        setVerifiedCustomer((prev) => ({
          ...prev,
          verifiedAt: data.customer.verifiedAt,
          phoneVerifiedAt: data.customer.phoneVerifiedAt,
        }));

        // If customer has any linked accounts, take the first one
        if (
          data.customer.customer_linked_accounts &&
          data.customer.customer_linked_accounts.length > 0
        ) {
          const account = data.customer.customer_linked_accounts[0];
          setWorkEmail({
            email: account.email,
            isOTPVisible: true,
            otpNumber: "",
            isVerified: account.verifiedAt !== null,
            linkedAccountId: account.id,
          });
        } else {
          // Reset work email if no linked accounts
          setWorkEmail({
            email: "",
            isOTPVisible: false,
            otpNumber: "",
            isVerified: false,
            linkedAccountId: null,
          });
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setCustomerDataLoader(false);
    }
  };

  useEffect(() => {
    getCustomerDetails();
  }, []);

  // Add this effect to sync verification status on component mount
  useEffect(() => {
    if (customer) {
      setVerifiedCustomer((prev) => ({
        ...prev,
        verifiedAt: customer.verifiedAt,
        phoneVerifiedAt: customer.phoneVerifiedAt,
      }));
    }
  }, [customer, setVerifiedCustomer]);

  const handleFormFieldChange = (event) => {
    const { name, value } = event.target;
    setCustomer((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === "name") {
      if (value?.trim().length < 3) {
        setNameError("Name should be at least 3 characters.");
      } else {
        setNameError("");
      }
    }
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    try {
      setEditProfileLoader(true);
      const {
        data: { success, data, message },
      } = await updateCustomerAPI({
        firstName: customer.firstName,
        lastName: customer.lastName,
        phoneNumber: customer.phoneNumber,
      });

      if (success) {
        saveAuthData(data.tokens);
        getCustomerDetails();
        setEnableEdit(false);
        setEditProfileLoader(false);
        showSnackbarWithMessage({
          type: "success",
          message: "Success! Your data has been updated.",
        });
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (error) {
      console.log("error", error);
      showSnackbarWithMessage({
        type: "error",
        message: error.message,
      });
    } finally {
      setEditProfileLoader(false);
    }
  };

  // Updated email verification flow - now using modal
  const handleReSendOTP = async () => {
    try {
      const {
        data: { success, message },
      } = await resendOtp(false, customer?.email);
      if (success) {
        // Set modal state for email verification
        setVerificationType("email");
        setVerificationIdentifier(customer?.email);
        setShowOtpModal(true);

        setVerificationState((prev) => ({
          ...prev,
          email: {
            ...prev.email,
            otpSent: true,
          },
        }));

        showSnackbarWithMessage({
          type: "success",
          message,
        });
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (e) {
      console.log(e.message);
      showSnackbarWithMessage({
        type: "error",
        message: e.message,
      });
    }
  };

  // Updated phone verification flow - now using modal
  const handlePhoneReSendOTP = async () => {
    setEditProfileLoader(true);
    try {
      const { data } = await resendOtp(false, customer?.email, customer?.phoneNumber);
      if (data?.success) {
        // Set modal state for phone verification
        setVerificationType("phone");
        setVerificationIdentifier(customer?.phoneNumber);
        setEditProfileLoader(false);
        setShowOtpModal(true);

        setVerificationState((prev) => ({
          ...prev,
          phone: {
            ...prev.phone,
            otpSent: true,
          },
        }));

        showSnackbarWithMessage({
          type: "success",
          message: data?.message,
        });
      } else {
        setEditProfileLoader(false);
        showSnackbarWithMessage({
          type: "error",
          message: data?.message,
        });
      }
    } catch (e) {
      console.log(e.message);
      showSnackbarWithMessage({
        type: "error",
        message: e.message,
      });
      setEditProfileLoader(false);
    }
  };

  // Handler for OTP verification
  const handleOtpVerify = async (otpCode) => {
    try {
      if (verificationType === "email") {
        const {
          data: { success, message },
        } = await verifyCustomerOtp({
          code: +otpCode,
          forgotPassword: false,
          userEmail: customer?.email,
        });

        if (success) {
          setVerifiedCustomer((prev) => ({
            ...prev,
            verifiedAt: new Date().toISOString(),
          }));

          setVerificationState((prev) => ({
            ...prev,
            email: {
              otpNumber: "",
              otpSent: false,
            },
          }));
          getCustomerDetails();
          setShowOtpModal(false);
          showSnackbarWithMessage({
            type: "success",
            message,
          });
        } else {
          showSnackbarWithMessage({
            type: "error",
            message,
          });
        }
      } else if (verificationType === "phone") {
        const {
          data: { success, message },
        } = await verifyCustomerOtp({
          code: +otpCode,
          forgotPassword: false,
          userNumber: customer?.phoneNumber,
        });

        if (success) {
          setVerifiedCustomer((prev) => ({
            ...prev,
            phoneVerifiedAt: new Date().toISOString(),
          }));

          setVerificationState((prev) => ({
            ...prev,
            phone: {
              otpNumber: "",
              otpSent: false,
            },
          }));
          getCustomerDetails();
          setShowOtpModal(false);
          showSnackbarWithMessage({
            type: "success",
            message,
          });
        } else {
          showSnackbarWithMessage({
            type: "error",
            message,
          });
        }
      } else if (verificationType === "secondary") {
        // Handle secondary email verification
        await handleVerify(otpCode);
      }
    } catch (e) {
      showSnackbarWithMessage({
        type: "error",
        message: e.message,
      });
    }
  };

  // Updated functions for work email verification using modal
  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setWorkEmail((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSendOTP = async () => {
    setEditProfileLoader(true);
    try {
      const { data } = await createLinkedAccountApi({ email: workEmail.email });
      if (data?.success) {
        setWorkEmail((prev) => ({
          ...prev,
          isOTPVisible: true,
          linkedAccountId: data.data.linkedAccount.id,
        }));

        // Show OTP modal for secondary email verification
        setVerificationType("secondary");
        setVerificationIdentifier(workEmail.email);
        setShowOtpModal(true);
        setEditProfileLoader(false);

        showSnackbarWithMessage({
          type: "success",
          message: data.message,
        });
      } else {
        showSnackbarWithMessage({
          type: "error",
          message: data?.message,
        });
        setEditProfileLoader(false);
      }
    } catch (e) {
      console.log(e.message);
      showSnackbarWithMessage({
        type: "error",
        message: e.message,
      });
    }
  };

  const handleVerify = async (otpCode) => {
    try {
      const {
        data: { success, message, data },
      } = await verifyLinkedAccountApi(
        { code: otpCode ? +otpCode : +workEmail.otpNumber },
        workEmail.linkedAccountId
      );

      if (success) {
        setWorkEmail((prev) => ({
          ...prev,
          isVerified: true,
        }));
        showSnackbarWithMessage({
          type: "success",
          message,
        });

        // Close modal if open
        setShowOtpModal(false);
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (e) {
      console.log(e.message);
      showSnackbarWithMessage({
        type: "error",
        message: e.message,
      });
    }
  };

  const handleUnlinkedAccount = async () => {
    try {
      setConfirmationModalLoader(true);
      const {
        data: { success, message },
      } = await unLinkedCustomerAccount(workEmail.linkedAccountId);

      if (success) {
        getCustomerDetails();

        // Reset the workEmail state
        setWorkEmail({
          email: "",
          otpNumber: "",
          isOTPVisible: false,
          linkedAccountId: "",
          isVerified: false,
        });

        showSnackbarWithMessage({
          type: "success",
          message,
        });
      }
      setConfirmationModalLoader(false);
    } catch (e) {
      console.log(e.message);
      showSnackbarWithMessage({
        type: "error",
        message: e.message,
      });
      setConfirmationModalLoader(false);
    }
  };
  return (
    <>
      <DashboardNavbar />
      <NavbarAlert isProfile={true} />
      <DashboardLayout>
        <MDBox
          sx={{
            mt: 10,
            "@media (max-width:767px)": {
              mt: 10,
            },
          }}
        >
          {customerDataLoader ? (
            <SimpleBackdrop />
          ) : (
            <Card
              sx={{
                p: 4,
                borderRadius: "8px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                "@media (max-width:767px)": {
                  p: 2,
                },
              }}
            >
              {/* Profile Header with Avatar */}
              <Box sx={{ mb: 4, display: "flex", alignItems: "center", maxWidth: "510px" }}>
                <Avatar src={profileImg} alt="Profile" sx={{ width: 64, height: 64, mr: 2 }} />
                <Typography sx={{ fontWeight: 400, fontSize: "20px", lineHeight: "32.5px" }}>
                  {customer?.firstName} {customer?.lastName}
                </Typography>
              </Box>

              {/* Profile Information Section with Action Buttons */}
              <form onSubmit={handleSubmitUpdate}>
                <Box
                  sx={{
                    maxWidth: "510px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 3,
                    mb: 3,
                  }}
                >
                  <Typography sx={{ fontWeight: 400, fontSize: "18px", lineHeight: "30px" }}>
                    Profile Information
                  </Typography>

                  {enableEdit ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => {
                          setEnableEdit(false);
                          getCustomerDetails();
                        }}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          px: 3,
                        }}
                      >
                        Cancel
                      </MDButton>
                      <MDButton
                        variant="contained"
                        color="success"
                        type="submit"
                        size="small"
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                        }}
                      >
                        Save
                      </MDButton>
                    </Box>
                  ) : (
                    <MDButton
                      variant="outlined"
                      color="success"
                      size="small"
                      onClick={() => setEnableEdit(true)}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                      }}
                    >
                      Edit Profile
                    </MDButton>
                  )}
                </Box>

                <Stack sx={{ maxWidth: "510px" }} spacing={"20px"}>
                  {/* First Name */}
                  <MDBox>
                    <MDTypography
                      sx={{
                        mb: "6px",
                        lineHeight: "20px",
                        color: "#414651",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      First Name
                    </MDTypography>
                    <MDInput
                      fullWidth
                      name="firstName"
                      value={customer?.firstName || ""}
                      onChange={handleFormFieldChange}
                      disabled={!enableEdit}
                      variant="outlined"
                      error={!!nameError}
                      required
                      helperText={nameError}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </MDBox>
                  {/* Last Name */}
                  <MDBox>
                    <MDTypography
                      sx={{
                        mb: "6px",
                        lineHeight: "20px",
                        color: "#414651",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      Last Name
                    </MDTypography>
                    <MDInput
                      fullWidth
                      name="lastName"
                      value={customer?.lastName || ""}
                      onChange={handleFormFieldChange}
                      disabled={!enableEdit}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </MDBox>
                  {/* Email */}
                  <Box>
                    <MDTypography
                      sx={{
                        mb: "6px",
                        lineHeight: "20px",
                        color: "#414651",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      Email {customer?.verifiedAt && "(default)"}
                    </MDTypography>
                    <Box sx={{ display: "flex", position: "relative" }}>
                      <MDInput
                        fullWidth
                        name="email"
                        value={customer?.email || ""}
                        disabled
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: !customer?.verifiedAt ? "8px 0px 0px 8px" : "8px",
                          },
                        }}
                      />
                      {customer?.email.trim() !== "" && (
                        <VerifiedStatus
                          isBtnVisible={customer?.verifiedAt ? false : true}
                          status={customer?.verifiedAt}
                        />
                      )}
                      {customer?.email?.trim() !== "" && !customer?.verifiedAt && (
                        <button
                          onClick={handleReSendOTP}
                          style={{
                            backgroundColor: "transparent",
                            color: "#414651",
                            border: "1px solid #D5D7DA",
                            cursor: "pointer",
                            padding: "8px 20px",
                            fontSize: "15px",
                            fontWeight: "600",
                            borderRadius: "0px 8px 8px 0px",
                            borderLeft: "0px solid",
                            textTransform: "none",
                            lineHeight: "24px",
                            transition: "all 0.2s ease",
                            outline: "none",
                          }}
                        >
                          Verify
                        </button>
                      )}
                    </Box>
                  </Box>
                  {/* Secondary Email */}
                  <Box>
                    <MDTypography
                      sx={{
                        mb: "6px",
                        lineHeight: "20px",
                        color: "#414651",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      Secondary Email (optional)
                    </MDTypography>
                    <Box sx={{ display: "flex", position: "relative" }}>
                      <SecondaryEmail
                        onFieldChange={handleFieldChange}
                        onSendOTP={handleSendOTP}
                        emailData={workEmail}
                        onVerify={() => handleVerify()}
                        index={0}
                        onUnlinked={handleUnlinkedAccount}
                        confirmationModalLoader={confirmationModalLoader}
                        isConfirmationModal={true}
                      />
                    </Box>
                  </Box>
                  {/* Mobile Phone Number */}
                  <Box>
                    <MDTypography
                      sx={{
                        mb: "6px",
                        lineHeight: "20px",
                        color: "#414651",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      Mobile Phone Number
                    </MDTypography>
                    <Box sx={{ display: "flex", position: "relative" }}>
                      <MDInput
                        fullWidth
                        name="phoneNumber"
                        value={customer?.phoneNumber || ""}
                        onChange={handleFormFieldChange}
                        disabled={!enableEdit}
                        variant="outlined"
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius:
                              customer?.phoneNumber &&
                              customer?.phoneNumber?.trim() !== "" &&
                              !customer?.phoneVerifiedAt
                                ? "8px 0px 0px 8px"
                                : "8px",
                          },
                        }}
                      />
                      {customer?.phoneNumber && customer?.phoneNumber?.trim() !== "" && (
                        <VerifiedStatus
                          isBtnVisible={customer?.phoneVerifiedAt ? false : true}
                          status={customer?.phoneVerifiedAt}
                        />
                      )}

                      {customer?.phoneNumber &&
                        customer?.phoneNumber?.trim() !== "" &&
                        !customer?.phoneVerifiedAt && (
                          <button
                            type="button"
                            onClick={handlePhoneReSendOTP}
                            style={{
                              backgroundColor: "transparent",
                              color: "#414651",
                              border: "1px solid #D5D7DA",
                              cursor: "pointer",
                              padding: "8px 20px",
                              fontSize: "15px",
                              fontWeight: "600",
                              borderRadius: "0px 8px 8px 0px",
                              borderLeft: "0px solid",
                              textTransform: "none",
                              lineHeight: "24px",
                              transition: "all 0.2s ease",
                              outline: "none",
                            }}
                          >
                            Verify
                          </button>
                        )}
                    </Box>
                  </Box>
                  {/* Password */}
                  <Box>
                    <MDTypography
                      sx={{
                        mb: "6px",
                        lineHeight: "20px",
                        color: "#414651",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      Password
                    </MDTypography>

                    {customer?.isPass === false ? (
                      <MDButton
                        variant="outlined"
                        color="success"
                        size="medium"
                        onClick={() => setOpenUpdatePasswordModal(true)}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                        }}
                      >
                        Set password
                      </MDButton>
                    ) : (
                      <MDButton
                        variant="outlined"
                        color="success"
                        size="medium"
                        onClick={() => setOpenUpdatePasswordModal(true)}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                        }}
                      >
                        Update password
                      </MDButton>
                    )}
                  </Box>
                </Stack>
              </form>
            </Card>
          )}

          {/* Modals */}
          {showOtpModal && (
            <OtpConfirmationModal
              identifier={verificationIdentifier}
              type={verificationType}
              open={showOtpModal}
              onClose={() => setShowOtpModal(false)}
              onVerify={handleOtpVerify}
              onSendOTP={() => handleSendOTP()}
            />
          )}

          <UpdatePasswordModal
            open={openUpdatePasswordModal}
            onConfirm={updatePassWord}
            loader={loader}
            userEmail={customer?.email}
            userPassword={userPassword}
            handleClose={handleClose}
            setLoader={setLoader}
            setUserPassword={setUserPassword}
            spinner={spinner}
            forgotPassword={customer?.isPass ? false : true}
          />
          {editProfileLoader && <SimpleBackdrop />}
        </MDBox>
      </DashboardLayout>

      <UnsavedChangesDialog />
    </>
  );
};

export default Profile;
